import React, {PureComponent} from "react"
import PropTypes from "prop-types"
import {get} from "lodash"

import withBranding, {WithBranding} from "@irati/brand/withBranding"
import {Deal, DealType, PaymentCurrencyType} from "@irati/types"
import {FormattedMessage} from "@irati/utils/lang"
import id from "@irati/utils/id"
import {determineDealThumbnailImage} from "@irati/deals/helpers/deals/appearance"
import {isDealUnavailable} from "@irati/deals/helpers/deals/isDealUnavailable"
import {COMPANY_BENEFITS as DEAL_TYPE_COMPANY_BENEFITS} from "@irati/deals/constants/dealType"
import {shouldDisplayDealAvatar} from "@irati-web/perks/src/utils/deals/imageUtils"
import isBoostedPerk from "@irati/deals/helpers/deals/isBoostedPerk"
import {RouterLink} from "@irati/ui/src/Link"
import Saving from "@irati-web/perks/src/components/Saving"
import {connect} from "@irati/store"
import Flex from "@irati/ui/src/Flex"
import {Body, H5} from "@irati/ui/src/Typography"
import BoostedAmount from "@irati-web/perks/src/components/BoostedSaving/BoostedAmount"
import getDealCurrency from "@irati/deals/helpers/deals/currency"
import dealSavingsType from "@irati/deals/types/enums/DealSavingsType"
import DealTypePill from "@irati-web/perks/src/utils/deals/dealTypePill"

import UnavailableDeal from "./UnavailableDeal"
import {
  AvatarContainer,
  Col,
  Container,
  DealBrandAvatar,
  EmptyWrapper,
  NonBoostedValue,
  SecondRow,
  StyledBadge,
  StyledIconText,
  StyledRouterLink
} from "./styled/DealResult"

interface Props extends WithBranding {
  deal: Deal
  badgeNumber: number
  proximityMeters: number
  className?: string
  onClick?: () => void
  isMobile?: boolean
  locations?: string[]
  shouldLinkToDealOnClick?: boolean
  showViewPerkDetailLink?: boolean
  onViewPerkDetailClick?: () => void
}

class DealResult extends PureComponent<Props> {
  static contextTypes = {
    appPath: PropTypes.string.isRequired
  }

  static defaultProps = {
    onClick: () => {},
    badgeNumber: null,
    proximityMeters: null,
    locations: null,
    shouldLinkToDealOnClick: true,
    className: null,
    showViewPerkDetailLink: false,
    onViewPerkDetailClick: () => {}
  }

  getWrapper = () => {
    const {onClick, shouldLinkToDealOnClick, deal} = this.props
    const {appPath} = this.context
    const wrapperProps = {
      onClick: () => onClick(deal)
    }
    if (shouldLinkToDealOnClick) {
      wrapperProps.id = id("link", this.props)
      wrapperProps.styled = false
      wrapperProps.to = `/info/${deal.urlSlug}`
      wrapperProps.app = appPath
    }
    return {
      component: shouldLinkToDealOnClick ? StyledRouterLink : EmptyWrapper,
      props: wrapperProps
    }
  }

  render() {
    const {
      deal,
      badgeNumber,
      proximityMeters,
      isMobile,
      shouldLinkToDealOnClick,
      className,
      showViewPerkDetailLink,
      onViewPerkDetailClick,
      branding
    } = this.props
    const {appPath} = this.context
    const savings = get(deal.savings, "userSavings", {})
    const thumbnailImage =
      deal.type === DealType.Merchandise || shouldDisplayDealAvatar(deal) ? determineDealThumbnailImage(deal, branding) : null
    const savingHeader =
      deal.type === DEAL_TYPE_COMPANY_BENEFITS ? <FormattedMessage id="perks.companyBenefits.savings.saveLabel" /> : savings.header
    const {component: Wrapper, props: wrapperProps} = this.getWrapper()

    const ViewPerkDetailLinkComponent = (
      <RouterLink app={appPath} to={`/info/${deal.urlSlug}`} onClick={onViewPerkDetailClick}>
        <StyledIconText icon="angleRight" iconPosition="right">
          <FormattedMessage id="platform.search.dealResult.viewPerkDetail.link.text" />
        </StyledIconText>
      </RouterLink>
    )

    const {userSavings, boostedSavings} = deal.savings
    const dealCurrency = getDealCurrency(deal)
    const isCurrencySaving = userSavings?.type === dealSavingsType.CURRENCY
    const isRewards = dealCurrency === PaymentCurrencyType.Rp

    return (
      <Container
        isBoostedPerk={!isRewards && isBoostedPerk(deal)}
        className={className}
        id={id("result", this.props)}
        isDealUnavailable={isDealUnavailable(deal)}>
        <Wrapper {...wrapperProps} isDealUnavailable={isDealUnavailable(deal)}>
          <Flex padding={thumbnailImage ? 0 : 2} margin="0 -4px">
            <Flex flex="1" marginRight={1}>
              <AvatarContainer>
                {thumbnailImage && <DealBrandAvatar id={id("logo", this.props)} src={thumbnailImage} small withShadow />}
                {badgeNumber && <StyledBadge>{badgeNumber}</StyledBadge>}
              </AvatarContainer>
            </Flex>
            <Flex flex="4" padding="0" direction={isDealUnavailable(deal) ? "column" : "row"}>
              <div>
                <Flex>
                  <DealTypePill deal={deal} />
                </Flex>
                <Body color="contrast">{deal.brandName}</Body>
                <H5 id={id("title", this.props)} wordBreak="break-word" marginBottom={1}>
                  {deal.header}
                </H5>
              </div>
              {isDealUnavailable(deal) && <UnavailableDeal />}
            </Flex>
            {!(isRewards || isDealUnavailable(deal)) && (
              <Flex flex="2" justifyContent="flex-end">
                {isBoostedPerk(deal) && (
                  <Flex alignItems="center">
                    <BoostedAmount value={userSavings.value} isCurrencySaving={isCurrencySaving} currency={dealCurrency} />
                    <NonBoostedValue>{boostedSavings.oldSavings.value}</NonBoostedValue>
                  </Flex>
                )}
                {!isBoostedPerk(deal) && (
                  <>
                    <Saving
                      type={userSavings.type}
                      header={savingHeader}
                      saving={userSavings.value}
                      currency={dealCurrency}
                      smallSavingsHeader
                      deal={deal}
                    />
                    {showViewPerkDetailLink && !isMobile && ViewPerkDetailLinkComponent}
                  </>
                )}
              </Flex>
            )}
          </Flex>
          {!isDealUnavailable(deal) && isMobile && (proximityMeters || !shouldLinkToDealOnClick) && (
            <SecondRow>
              <Col>{showViewPerkDetailLink && ViewPerkDetailLinkComponent}</Col>
            </SecondRow>
          )}
        </Wrapper>
      </Container>
    )
  }
}

const mapStateToProps = ({mq}) => ({isMobile: mq.lessThan.medium})

export default connect(mapStateToProps)(withBranding(DealResult))
